import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, UseFormSetValue } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import {
  Badge,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  FilterListOutlined as FilterListOutlinedIcon,
  Add as AddIcon,
  DeleteOutlineRounded as DeleteIcon,
} from '@mui/icons-material';

import ChipSelectMenu from '../../forms/ChipSelectMenu';
import TextInput from '../../forms/TextInput';
import DateInput from '../../forms/DateInput';

const PREFIX = 'dataGridFilterMenu';

const classes = {
  container: `${PREFIX}-container`,
  filterContent: `${PREFIX}-filterContent`,
  filterIcon: `${PREFIX}-filterIcon`,
  filterText: `${PREFIX}-filterText`,
  filterMenu: `${PREFIX}-filterMenu`,
  filterMenuContent: `${PREFIX}-filterMenuContent`,
  filterMenuItem: `${PREFIX}-filterMenuItem`,
  filterLabel: `${PREFIX}-filterLabel`,
  filterSelect: `${PREFIX}-filterSelect`,
  addFilterButton: `${PREFIX}-addFilterButton`,
  removeFilterButton: `${PREFIX}-removeFilterButton`,
};

const StyledFilterContainer = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: '114px',
    height: '100%',
    background: '#FFFFFF',
    boxShadow: '0px 2px 7px 0px #01101226',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  [`& .${classes.filterContent}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    height: '100%',
    padding: '12px 22px',
  },
  [`& .${classes.filterIcon}`]: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  [`& .${classes.filterText}`]: {
    margin: 0,
    marginTop: '-0.8px',
    padding: 0,
    cursor: 'pointer',
    color: '#01101299',
    fontFamily: 'Source Sans Pro',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.46px',
    textAlign: 'left',
    textUnderlinePosition: 'from-font',
    textDecorationSkipInk: 'none',
  },
  [`& .${classes.filterMenu}`]: {
    // minWidth: '400px',
    border: '1px solid #f00',
  },
}));

const StyledFilterMenu = styled(Popover)(({ theme }) => ({
  marginTop: theme.spacing(1),
  [`& .${classes.filterMenuContent}`]: {
    boxSizing: 'border-box',
    padding: theme.spacing(3),
    width: '584px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  [`& .${classes.filterSelect}`]: {
    width: '190px',
    maxWidth: '190px',
  },
  [`& .${classes.removeFilterButton}`]: {
    marginLeft: 'auto',
    marginRight: 0,
  },
  [`& .${classes.filterMenuItem}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    width: '100%',
    [`& div:nth-of-type(2)`]: {
      flexGrow: 1,
    },
    [`& div:nth-of-type(3)`]: {
      flexGrow: 1,
    },
  },
  [`& .${classes.filterLabel}`]: {
    paddingRight: theme.spacing(2),
  },
  [`& .${classes.filterSelect}`]: {
    minWidth: '200px',
    '& .MuiSelect-select': {
      padding: '8px 14px',
    },
  },
  [`& .${classes.addFilterButton}`]: {
    marginTop: theme.spacing(1),
    padding: 0,
  },
}));

type DataGridFilter = {
  label: string;
  name: string;
  value?: string;
  options?: { label: string; value: string }[];
  type:
    | 'text'
    | 'select'
    | 'multi-select'
    | 'combo-box'
    | 'date'
    | 'date-range';
};

type DataGridFilterMenuProps = {
  label: string;
  filters: DataGridFilter[];
  onFilterChange: (filter: string) => void;
  control: Control<any>;
  resetField: (name: string) => void;
  setValue: UseFormSetValue<any>;
};

const DataGridFilterMenu = ({
  label,
  filters,
  onFilterChange,
  control,
  resetField,
  setValue,
}: DataGridFilterMenuProps) => {
  const { t } = useTranslation();
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [activeFilters, setActiveFilters] = useState<DataGridFilter[]>([]);
  const [showFilterSelector, setShowFilterSelector] = useState(true);

  const remainingFilters = filters?.filter(
    (filter) => !activeFilters.find((f) => f.name === filter.name),
  );

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterMenuOpen(true);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
    setFilterMenuOpen(false);
  };

  const handleActiveFilterChange = (
    event: SelectChangeEvent<string>,
    previousFilterName: string,
  ) => {
    const value = event.target.value;
    const selectedFilter = remainingFilters?.find((f) => f.name === value);
    const previousFilterIndex = activeFilters.findIndex(
      (f) => f.name === previousFilterName,
    );
    if (selectedFilter && previousFilterIndex >= 0) {
      resetField(previousFilterName);
      const newActiveFilters = [...activeFilters];
      newActiveFilters.splice(previousFilterIndex, 1, selectedFilter);
      setActiveFilters(newActiveFilters);
    }
  };

  const handleFilterSelectorChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setShowFilterSelector(false);
    const selectedFilter = remainingFilters?.find((f) => f.name === value);
    if (selectedFilter) {
      setActiveFilters([...activeFilters, selectedFilter]);
    }
  };

  const handleAddFilterRow = () => {
    setShowFilterSelector(true);
  };

  const handleRemoveFilter = (filter: DataGridFilter) => {
    if (activeFilters.length === 1) {
      setShowFilterSelector(true);
    }
    resetField(filter.name);
    setActiveFilters(activeFilters.filter((f) => f.name !== filter.name));
  };

  return (
    <StyledFilterContainer className={classes.container}>
      <Badge badgeContent={activeFilters.length} color="primary">
        <Button
          className={classes.filterContent}
          onClick={handleFilterMenuOpen}>
          <div className={classes.filterIcon}>
            <FilterListOutlinedIcon sx={{ color: '#0110128A' }} />
          </div>
          <p className={classes.filterText}>{label}</p>
        </Button>
      </Badge>
      <StyledFilterMenu
        open={filterMenuOpen}
        className={classes.filterMenu}
        onClose={handleFilterMenuClose}
        anchorEl={filterAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <div className={classes.filterMenuContent}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            {activeFilters?.map((filter) => (
              <div
                className={classes.filterMenuItem}
                key={`active-filter-${filter.name}`}>
                <Select
                  name={filter.name}
                  value={filter.name}
                  defaultValue={filter.name}
                  className={classes.filterSelect}
                  onChange={(event) =>
                    handleActiveFilterChange(event, filter.name)
                  }>
                  <MenuItem key={filter.name} value={filter.name}>
                    {filter.label}
                  </MenuItem>
                  {remainingFilters.map((filter) => (
                    <MenuItem key={filter.name} value={filter.name}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
                {filter.type === 'text' && (
                  <TextInput
                    name={filter.name}
                    label={filter.label}
                    control={control}
                    defaultValue={filter.value}
                  />
                )}
                {filter.type === 'multi-select' && (
                  <ChipSelectMenu
                    name={filter.name}
                    label={filter.label}
                    values={filter.options || []}
                    valueAttr="value"
                    labelAttr="label"
                    control={control}
                  />
                )}
                {filter.type === 'date' && (
                  <DateInput
                    name={filter.name}
                    label={filter.label}
                    control={control}
                    defaultValue={filter.value}
                  />
                )}
                {filter.type === 'date-range' && (
                  <>
                    <DateInput
                      name={`${filter.name}[from]`}
                      label={t('common.from')}
                      control={control}
                      onChange={(date) => {
                        setValue(`${filter.name}[from]`, date);
                      }}
                    />
                    <DateInput
                      name={`${filter.name}[to]`}
                      label={t('common.to')}
                      control={control}
                      onChange={(date) => {
                        setValue(`${filter.name}[to]`, date);
                      }}
                    />
                  </>
                )}
                <IconButton
                  className={classes.removeFilterButton}
                  onClick={() => handleRemoveFilter(filter)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
            {showFilterSelector && (
              <div className={classes.filterMenuItem}>
                <Select
                  value={''}
                  className={classes.filterSelect}
                  label="Select"
                  onChange={handleFilterSelectorChange}>
                  {remainingFilters?.map((filter) => (
                    <MenuItem key={filter.name} value={filter.name}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
            {remainingFilters && remainingFilters.length > 0 && (
              <Button
                className={classes.addFilterButton}
                variant="text"
                startIcon={<AddIcon />}
                onClick={handleAddFilterRow}>
                <Typography variant="body1" color="primary">
                  {t('common.addFilter')}
                </Typography>
              </Button>
            )}
          </LocalizationProvider>
        </div>
      </StyledFilterMenu>
    </StyledFilterContainer>
  );
};

export { DataGridFilterMenu };
export type { DataGridFilter, DataGridFilterMenuProps };
